/**
 * Shared Marketplace Types
 * Types used by both public marketplace pages and admin dashboard
 * This file should NOT import from any admin-specific modules
 */

// ============================================
// Book Pricing Types
// ============================================

/**
 * Book pricing type
 */
export enum TBookPricingType {
    FREE = 'free',
    PAID = 'paid',
}

/**
 * Supported currencies
 * Includes major world currencies for global marketplace support
 */
export enum TCurrency {
    INR = 'INR', // Indian Rupee
    USD = 'USD', // US Dollar
    CAD = 'CAD', // Canadian Dollar
    EUR = 'EUR', // Euro
    GBP = 'GBP', // British Pound
    JPY = 'JPY', // Japanese Yen
    AUD = 'AUD', // Australian Dollar
    AED = 'AED', // UAE Dirham
    SGD = 'SGD', // Singapore Dollar
    CHF = 'CHF', // Swiss Franc
}

/**
 * Currency metadata interface
 */
export interface ICurrencyInfo {
    code: TCurrency
    symbol: string
    name: string
    /** Number of decimal places (0 for JPY, 2 for most others) */
    decimals: number
    /** Locale for number formatting */
    locale: string
    /** Position of currency symbol */
    symbolPosition: 'before' | 'after'
}

/**
 * Currency information lookup
 */
export const CURRENCY_INFO: Record<TCurrency, ICurrencyInfo> = {
    [TCurrency.INR]: {
        code: TCurrency.INR,
        symbol: '₹',
        name: 'Indian Rupee',
        decimals: 2,
        locale: 'en-IN',
        symbolPosition: 'before',
    },
    [TCurrency.USD]: {
        code: TCurrency.USD,
        symbol: '$',
        name: 'US Dollar',
        decimals: 2,
        locale: 'en-US',
        symbolPosition: 'before',
    },
    [TCurrency.CAD]: {
        code: TCurrency.CAD,
        symbol: 'CA$',
        name: 'Canadian Dollar',
        decimals: 2,
        locale: 'en-CA',
        symbolPosition: 'before',
    },
    [TCurrency.EUR]: {
        code: TCurrency.EUR,
        symbol: '€',
        name: 'Euro',
        decimals: 2,
        locale: 'de-DE',
        symbolPosition: 'after',
    },
    [TCurrency.GBP]: {
        code: TCurrency.GBP,
        symbol: '£',
        name: 'British Pound',
        decimals: 2,
        locale: 'en-GB',
        symbolPosition: 'before',
    },
    [TCurrency.JPY]: {
        code: TCurrency.JPY,
        symbol: '¥',
        name: 'Japanese Yen',
        decimals: 0, // No fractional units
        locale: 'ja-JP',
        symbolPosition: 'before',
    },
    [TCurrency.AUD]: {
        code: TCurrency.AUD,
        symbol: 'A$',
        name: 'Australian Dollar',
        decimals: 2,
        locale: 'en-AU',
        symbolPosition: 'before',
    },
    [TCurrency.AED]: {
        code: TCurrency.AED,
        symbol: 'د.إ',
        name: 'UAE Dirham',
        decimals: 2,
        locale: 'ar-AE',
        symbolPosition: 'after',
    },
    [TCurrency.SGD]: {
        code: TCurrency.SGD,
        symbol: 'S$',
        name: 'Singapore Dollar',
        decimals: 2,
        locale: 'en-SG',
        symbolPosition: 'before',
    },
    [TCurrency.CHF]: {
        code: TCurrency.CHF,
        symbol: 'CHF',
        name: 'Swiss Franc',
        decimals: 2,
        locale: 'de-CH',
        symbolPosition: 'before',
    },
}

/**
 * Book pricing configuration
 */
export interface IBookPricing {
    type: TBookPricingType
    /** Price in smallest currency unit (paisa for INR, cents for USD, etc.) */
    amount: number
    currency: TCurrency
    /** Original price for showing discounts */
    originalAmount?: number
}

// ============================================
// Demo/Preview Types
// ============================================

/**
 * Demo mode for books
 */
export enum TDemoMode {
    FIRST_N_PAGES = 'first_n_pages',
    SPECIFIC_CATEGORIES = 'specific_categories',
    NO_DEMO = 'no_demo',
}

/**
 * Demo settings for a book
 */
export interface IDemoSettings {
    enabled: boolean
    mode: TDemoMode
    freePageCount?: number
    freeCategories?: string[]
}

// ============================================
// CTA Button Types
// ============================================

/**
 * Predefined CTA button texts
 */
export enum TCTAButtonPreset {
    BUY_NOW = 'Buy Now',
    UNLOCK_FULL_JOURNEY = 'Unlock Full Journey',
    GET_FULL_ACCESS = 'Get Full Access',
    DISCOVER_MORE = 'Discover More',
    PURCHASE_BOOK = 'Purchase Book',
    CUSTOM = 'custom',
}

/**
 * CTA settings for demo pages
 */
export interface ICTASettings {
    buttonText: TCTAButtonPreset | string
    customText?: string
    /** Explicit customization for "Buy Now" button */
    buyNowButtonText?: string
    showOnDemoPages: boolean
    showOnLockedPages: boolean
    subtitle?: string
}

// ============================================
// GEO / Country Types
// ============================================

/** Country-to-currency mapping for GEO features */
export const COUNTRY_TO_CURRENCY: Record<string, TCurrency> = {
    US: TCurrency.USD,
    IN: TCurrency.INR,
    CA: TCurrency.CAD,
    AU: TCurrency.AUD,
    NZ: TCurrency.AUD,
    GB: TCurrency.GBP,
    DE: TCurrency.EUR,
    FR: TCurrency.EUR,
    ES: TCurrency.EUR,
    IT: TCurrency.EUR,
    NL: TCurrency.EUR,
    BE: TCurrency.EUR,
    AT: TCurrency.EUR,
    PT: TCurrency.EUR,
    IE: TCurrency.EUR,
    FI: TCurrency.EUR,
    GR: TCurrency.EUR,
    LU: TCurrency.EUR,
    SE: TCurrency.EUR,
    NO: TCurrency.USD,
    DK: TCurrency.EUR,
    PL: TCurrency.EUR,
    JP: TCurrency.JPY,
    AE: TCurrency.AED,
    SA: TCurrency.AED,
    QA: TCurrency.AED,
    BH: TCurrency.AED,
    OM: TCurrency.AED,
    KW: TCurrency.AED,
    SG: TCurrency.SGD,
    CH: TCurrency.CHF,
}

/** Get currency for a country code, defaults to USD */
export function getCurrencyForCountry(countryCode: string | null | undefined): TCurrency {
    if (!countryCode) {
        return TCurrency.USD
    }
    return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] ?? TCurrency.USD
}

/** Country list with labels for signup dropdown */
export const COUNTRIES = [
    { code: 'US', label: 'United States' },
    { code: 'IN', label: 'India' },
    { code: 'GB', label: 'United Kingdom' },
    { code: 'CA', label: 'Canada' },
    { code: 'AU', label: 'Australia' },
    { code: 'NZ', label: 'New Zealand' },
    { code: 'DE', label: 'Germany' },
    { code: 'FR', label: 'France' },
    { code: 'NL', label: 'Netherlands' },
    { code: 'ES', label: 'Spain' },
    { code: 'IT', label: 'Italy' },
    { code: 'BE', label: 'Belgium' },
    { code: 'AT', label: 'Austria' },
    { code: 'PT', label: 'Portugal' },
    { code: 'IE', label: 'Ireland' },
    { code: 'FI', label: 'Finland' },
    { code: 'GR', label: 'Greece' },
    { code: 'LU', label: 'Luxembourg' },
    { code: 'SE', label: 'Sweden' },
    { code: 'NO', label: 'Norway' },
    { code: 'DK', label: 'Denmark' },
    { code: 'PL', label: 'Poland' },
    { code: 'CH', label: 'Switzerland' },
    { code: 'JP', label: 'Japan' },
    { code: 'SG', label: 'Singapore' },
    { code: 'AE', label: 'United Arab Emirates' },
    { code: 'SA', label: 'Saudi Arabia' },
    { code: 'QA', label: 'Qatar' },
    { code: 'BH', label: 'Bahrain' },
    { code: 'OM', label: 'Oman' },
    { code: 'KW', label: 'Kuwait' },
] as const

// ============================================
// Default Values
// ============================================

export const DEFAULT_PRICING: IBookPricing = {
    type: TBookPricingType.FREE,
    amount: 0,
    currency: TCurrency.INR,
}

export const DEFAULT_DEMO_SETTINGS: IDemoSettings = {
    enabled: false,
    mode: TDemoMode.NO_DEMO,
    freePageCount: 3,
}

export const DEFAULT_CTA_SETTINGS: ICTASettings = {
    buttonText: TCTAButtonPreset.UNLOCK_FULL_JOURNEY,
    showOnDemoPages: true,
    showOnLockedPages: true,
}

// ============================================
// Helper Functions
// ============================================

/**
 * Format currency amount for display
 * Converts from smallest unit (paisa/cents) to display value with proper formatting
 */
export function formatCurrencyAmount(amount: number, currency: TCurrency = TCurrency.INR): string {
    const info = CURRENCY_INFO[currency]
    const divisor = Math.pow(10, info.decimals)
    const value = amount / divisor

    // Format the number with proper locale
    const formattedNumber = value.toLocaleString(info.locale, {
        minimumFractionDigits: info.decimals,
        maximumFractionDigits: info.decimals,
    })

    // Position the symbol correctly
    if (info.symbolPosition === 'before') {
        return `${info.symbol}${formattedNumber}`
    }
    return `${formattedNumber} ${info.symbol}`
}

/**
 * Legacy alias for formatCurrencyAmount
 * @deprecated Use formatCurrencyAmount instead
 */
export function formatPrice(amount: number, currency: TCurrency = TCurrency.INR): string {
    return formatCurrencyAmount(amount, currency)
}

/**
 * Convert display amount to smallest unit (paisa/cents/etc.)
 */
export function toSmallestUnit(displayAmount: number, currency: TCurrency = TCurrency.INR): number {
    const info = CURRENCY_INFO[currency]
    const multiplier = Math.pow(10, info.decimals)
    return Math.round(displayAmount * multiplier)
}

/**
 * Convert smallest unit (paisa/cents/etc.) to display amount
 */
export function fromSmallestUnit(
    smallestUnit: number,
    currency: TCurrency = TCurrency.INR,
): number {
    const info = CURRENCY_INFO[currency]
    const divisor = Math.pow(10, info.decimals)
    return smallestUnit / divisor
}

/**
 * Legacy alias for toSmallestUnit
 * @deprecated Use toSmallestUnit instead
 */
export function toPaisa(rupees: number): number {
    return toSmallestUnit(rupees, TCurrency.INR)
}

/**
 * Legacy alias for fromSmallestUnit
 * @deprecated Use fromSmallestUnit instead
 */
export function fromPaisa(paisa: number): number {
    return fromSmallestUnit(paisa, TCurrency.INR)
}

/**
 * Get currency info by code
 */
export function getCurrencyInfo(currency: TCurrency): ICurrencyInfo {
    return CURRENCY_INFO[currency]
}

/**
 * Get all available currencies as options (for dropdowns)
 */
export function getCurrencyOptions(): Array<{ value: TCurrency; label: string; symbol: string }> {
    return Object.values(TCurrency).map(code => {
        const info = CURRENCY_INFO[code]
        return {
            value: code,
            label: `${info.name} (${info.symbol})`,
            symbol: info.symbol,
        }
    })
}

/**
 * Calculate seller earnings after platform fee
 */
export function calculateSellerEarnings(
    amount: number,
    platformFeePercent: number = 15,
): { platformFee: number; sellerEarnings: number } {
    const platformFee = Math.round((amount * platformFeePercent) / 100)
    return {
        platformFee,
        sellerEarnings: amount - platformFee,
    }
}
